import { Component, computed, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { getZipcodeFormErrors } from '../../../../store/form-messages';
import { Store } from '@ngrx/store';
import { AppStore } from '@aaa/emember/store-types';
import {
  getZipCodeInClubFailed,
  getZipCodeInClubIsLoading,
  getZipCodeInClubSuccess,
  ValidatorsActions,
} from '@aaa/emember/store-validators';
import { ValidatorService } from '../../../share/form/validators/validator.service';
import { filter, Subject, takeUntil, tap } from 'rxjs';
import { CookiesService } from '../../../share/services/cookies.service';
import { ClubApp } from '../../../../types';
import { AvaDividerComponent } from '../../../share/ava-divider/ava-divider.component';
import { ButtonComponent } from '../../../share/ava-button/ava-button.component';
import { CommonModule } from '@angular/common';
import { FormInputComponent } from '../../../share/form/controls/input';
import { environment } from '../../../../../environments/environment';
import { getZipcodeDescription } from '../../../share/constants/zipcode-description';
import { clubOption } from '../../../../signals';

@Component({
  standalone: true,
  selector: 'ava-zipcode-form',
  template: `
    <div class="tw-flex tw-flex-col tw-gap-4">
      <div class="tw-flex">
        <p class="tw-text-[22px] tw-font-normal tw-text-blue-light">
          Welcome to AAA {{ clubOption().name }} {{ clubOption().clubId !== ClubApp.Northway ? 'County' : '' }}
        </p>
        <img
          width="40%"
          src="https://www.hoosier.aaa.com/sites/all/themes/aaav3/images/aaa-logo.png"
          alt="Welcome to AAA {{ clubOption().name }} {{ clubOption().clubId !== ClubApp.Northway ? 'County' : '' }}"
        />
      </div>
      <p class="tw-flex tw-text-base tw-font-normal">
        To enjoy customized local content, please enter your ZIP Code below.
      </p>
      <form [formGroup]="formGroup">
        <div class="tw-flex tw-gap-4">
          <ava-form-input
            [errorMessages]="errorMessages().zipcode"
            beforeLabel="Zip Code"
            placeholder="zipcode"
            formControlName="zipcode"
            class="tw-flex"
          />
          <ava-button
            type="primary"
            label="Go"
            [isLoading]="isLoading$ | async"
            [disabled]="formGroup.invalid"
            (clicked)="submitForm()"
          />
        </div>
        @if (failed$ | async) {
          <div class="tw-text-red-500" [innerHTML]="errorMessages().zipcode?.zipcodeIsNotInsideClub"></div>
        }
      </form>
      <ava-divider />
      <p [innerHTML]="zipcodeFormDescription()" class="ava-text"></p>
    </div>
    <div class="tw-flex tw-bg-white tw-p-4"></div>
  `,
  host: {
    class: 'tw-flex tw-p-4 tw-bg-white',
  },
  imports: [AvaDividerComponent, ButtonComponent, CommonModule, FormInputComponent, ReactiveFormsModule],
})
export class ZipcodeForm implements OnInit, OnDestroy {
  readonly ClubApp = ClubApp;
  readonly errorMessages = getZipcodeFormErrors;
  readonly clubOption = computed(() => clubOption());

  store = inject(Store<AppStore>);
  fb = inject(FormBuilder);
  validators = inject(ValidatorService);
  cookies = inject(CookiesService);
  alive$ = new Subject();
  formGroup = this.initFormGroup();
  zipcodeFormDescription = computed(() => getZipcodeDescription(clubOption().clubId));
  isLoading$ = this.store.select(getZipCodeInClubIsLoading);
  success$ = this.store.select(getZipCodeInClubSuccess);
  failed$ = this.store.select(getZipCodeInClubFailed);

  ngOnInit() {
    this.success$
      .pipe(
        filter(() => !!this.formGroup.value.zipcode),
        tap(() => this.cookies.setZipCode(String(this.formGroup.value.zipcode), environment.clubId)),
        takeUntil(this.alive$),
      )
      .subscribe();
  }

  submitForm() {
    const { zipcode = '' } = this.formGroup.value;

    if (zipcode) {
      this.store.dispatch(ValidatorsActions.zipCodeInClub({ zipcode }));
    }
  }

  initFormGroup() {
    return this.fb.group(
      {
        zipcode: new FormControl('', {
          nonNullable: true,
          validators: [this.validators.zipCodeFormat()],
        }),
      },
      { updateOn: 'blur' },
    );
  }

  ngOnDestroy() {
    this.alive$.next(null);
    this.alive$.complete();
  }
}
